import { useSelector } from 'react-redux';
import { DataSink } from 'new/entities/DataSink';
import DateUtils from 'js/lib/DateUtils';
import { useEffect } from 'react';
import { fetchResourceMetricsAggregate, fetchResourceMetricsPerDay } from 'js/actions';
import { RESOURCE_TYPE_SINK } from 'js/constants/resourceTypes';
import { MetricsUtil } from 'new/utils/MetricsUtil';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_RESOURCE_METRICS_AGGREGATE_REQUEST,
  GET_RESOURCE_METRICS_BY_DAYS_REQUEST,
} from 'js/constants/actionTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { MetricsApiDateRange } from '@modules/monitor/resource-triage/components/MetricsTrendPanel';

const NoResourceResponse = {
  loading: false,
  metricsByDate: null,
  lifeMetrics: MetricsUtil.handleLifetimeMetrics(null),
  credentials: null,
  loadResourceMetricsAggregate: () => {},
  loadResourceMetricsPerDay: () => {},
};

export function useDestinationOverviewMetrics(resource: DataSink) {
  const dispatch = useAppDispatch();
  const metrics = useSelector((state: any) => state.metrics);
  const loading = useIsLoading(
    GET_RESOURCE_METRICS_BY_DAYS_REQUEST,
    GET_RESOURCE_METRICS_AGGREGATE_REQUEST,
  );
  const resourceID = resource?.id;

  useEffect(() => {
    if (resourceID) {
      dispatch(fetchResourceMetricsAggregate(resourceID, RESOURCE_TYPE_SINK));
      dispatch(
        fetchResourceMetricsPerDay(resourceID, RESOURCE_TYPE_SINK, DateUtils.getPastDateRange(30)),
      );
    }
  }, []);

  if (!resource || typeof resource !== 'object') return NoResourceResponse;

  const loadResourceMetricsAggregate = () =>
    dispatch(fetchResourceMetricsAggregate(resourceID, RESOURCE_TYPE_SINK));

  const loadResourceMetricsPerDay = (range: MetricsApiDateRange) => {
    if (range) {
      dispatch(fetchResourceMetricsPerDay(resourceID, RESOURCE_TYPE_SINK, range));
    }
  };

  const sinkMetrics = metrics[RESOURCE_TYPE_SINK];
  const lifeMetrics = sinkMetrics[resourceID];

  const aggregateMetrics = lifeMetrics?.agg?.metrics ?? null;
  const metricsByDate =
    lifeMetrics?.byDate && Object.keys(lifeMetrics.byDate).length !== 0 ? lifeMetrics.byDate : null;

  return {
    loading,
    metricsByDate,
    loadResourceMetricsAggregate,
    loadResourceMetricsPerDay,
    lifeMetrics: MetricsUtil.handleLifetimeMetrics(aggregateMetrics),
  };
}
